import type {
  ActivityKind,
  ActivityPerson,
  ActivityRow,
  UserActivityReport,
} from "../../zodios/apiDefinitions/api/staffUserActivity";

export type Audience = "team" | "trellis" | "everyone";
export type PersonStatus = "active" | "slowing" | "inactive" | "never" | "invited";
export type FeatureId =
  | "dashboard"
  | "campaigns"
  | "keywords"
  | "rules"
  | "products"
  | "categories"
  | "pricing"
  | "reports"
  | "saved_views"
  | "insights"
  | "creative";

const DAY_MS = 24 * 60 * 60 * 1000;
const ACTIVE_WITHIN_DAYS = 7;
const SLOWING_WITHIN_DAYS = 30;

const CHANGE_KINDS: ReadonlySet<ActivityKind> = new Set<ActivityKind>([
  "campaign_change",
  "keyword_change",
  "rule_change",
]);
const REPORT_KINDS: ReadonlySet<ActivityKind> = new Set<ActivityKind>(["export", "amc_report"]);

/** The order the "who did the work" chart lists actions in: changes first, since they are what move an account. */
const WORK_KINDS: ActivityKind[] = [
  "campaign_change",
  "keyword_change",
  "rule_change",
  "export",
  "amc_report",
  "saved_view",
  "insight",
  "creative",
];

const FEATURE_MATCHERS: { id: FeatureId; matches: (row: ActivityRow) => boolean }[] = [
  { id: "dashboard", matches: (row) => row.kind === "page_view" && row.detail === "dashboard" },
  {
    id: "campaigns",
    matches: (row) => (row.kind === "page_view" && row.detail === "ads") || row.kind === "campaign_change",
  },
  { id: "keywords", matches: (row) => row.kind === "keyword_change" },
  { id: "rules", matches: (row) => row.kind === "rule_change" && row.detail !== "pricing" },
  { id: "products", matches: (row) => row.kind === "page_view" && row.detail === "product" },
  { id: "categories", matches: (row) => row.kind === "page_view" && row.detail === "category" },
  {
    id: "pricing",
    matches: (row) =>
      (row.kind === "page_view" && row.detail === "plan") || (row.kind === "rule_change" && row.detail === "pricing"),
  },
  { id: "reports", matches: (row) => REPORT_KINDS.has(row.kind) },
  { id: "saved_views", matches: (row) => row.kind === "saved_view" },
  { id: "insights", matches: (row) => row.kind === "insight" },
  { id: "creative", matches: (row) => row.kind === "creative" },
];

export function featureOf(row: ActivityRow): FeatureId | null {
  return FEATURE_MATCHERS.find((feature) => feature.matches(row))?.id ?? null;
}

interface Totals {
  views: number;
  changes: number;
  reports: number;
  all: number;
}

export interface PersonStats {
  person: ActivityPerson;
  status: PersonStatus;
  lastSeen: Date | null;
  current: Totals;
  previous: Totals;
  activeDays: number;
  previousActiveDays: number;
  /** Everything the person did on each day of the period, oldest first. */
  daily: number[];
  topFeature: FeatureId | null;
}

interface DailyPoint {
  date: string;
  activePeople: number;
  changes: number;
  views: number;
}

export interface Comparison {
  current: number;
  previous: number;
}

export interface ReportSummary {
  days: string[];
  previousDays: string[];
  people: PersonStats[];
  peopleWithAccess: number;
  activePeople: Comparison;
  activeDaysPerPerson: Comparison;
  changes: Comparison;
  views: Comparison;
  daily: DailyPoint[];
  previousDaily: DailyPoint[];
  features: { id: FeatureId; people: number; actions: number }[];
  /** Day of week (Monday first) by local hour. */
  heatmap: number[][];
  topChangeKind: ActivityKind | null;
  longestAway: PersonStats | null;
}

export interface WorkSplit {
  kind: ActivityKind;
  team: number;
  trellis: number;
}

export function eachDay(start: string, end: string): string[] {
  const days: string[] = [];
  for (let time = Date.parse(`${start}T00:00:00Z`); time <= Date.parse(`${end}T00:00:00Z`); time += DAY_MS) {
    days.push(new Date(time).toISOString().slice(0, 10));
  }
  return days;
}

const dayOf = (row: ActivityRow) => new Date(row.hour).toISOString().slice(0, 10);

function inAudience(person: ActivityPerson, audience: Audience) {
  if (audience === "everyone") return true;
  return person.is_trellis === (audience === "trellis");
}

function emptyTotals(): Totals {
  return { views: 0, changes: 0, reports: 0, all: 0 };
}

function addTo(totals: Totals, row: ActivityRow) {
  totals.all += row.count;
  if (row.kind === "page_view") totals.views += row.count;
  if (CHANGE_KINDS.has(row.kind)) totals.changes += row.count;
  if (REPORT_KINDS.has(row.kind)) totals.reports += row.count;
}

function statusFor(person: ActivityPerson, lastSeen: Date | null, now: Date): PersonStatus {
  if (!lastSeen) return person.membership_status === "invited" ? "invited" : "never";
  const daysAway = (now.getTime() - lastSeen.getTime()) / DAY_MS;
  if (daysAway <= ACTIVE_WITHIN_DAYS) return "active";
  if (daysAway <= SLOWING_WITHIN_DAYS) return "slowing";
  return "inactive";
}

export function daysSince(date: Date, now: Date) {
  return Math.max(0, Math.floor((now.getTime() - date.getTime()) / DAY_MS));
}

function latest(dates: (Date | null)[]): Date | null {
  return dates.reduce<Date | null>((max, date) => (date && (!max || date > max) ? date : max), null);
}

function personStats(
  person: ActivityPerson,
  rows: ActivityRow[],
  days: string[],
  previousDays: Set<string>,
  now: Date,
): PersonStats {
  const current = emptyTotals();
  const previous = emptyTotals();
  const dayIndex = new Map(days.map((day, index) => [day, index]));
  const daily = days.map(() => 0);
  const currentDays = new Set<string>();
  const earlierDays = new Set<string>();
  const featureCounts = new Map<FeatureId, number>();
  let lastActivity: Date | null = null;

  for (const row of rows) {
    const day = dayOf(row);
    const index = dayIndex.get(day);
    const hour = new Date(row.hour);
    if (!lastActivity || hour > lastActivity) lastActivity = hour;
    if (index !== undefined) {
      addTo(current, row);
      daily[index] = (daily[index] ?? 0) + row.count;
      currentDays.add(day);
      const feature = featureOf(row);
      if (feature) featureCounts.set(feature, (featureCounts.get(feature) ?? 0) + row.count);
    } else if (previousDays.has(day)) {
      addTo(previous, row);
      earlierDays.add(day);
    }
  }

  const lastSeen = latest([lastActivity, person.last_login ? new Date(person.last_login) : null]);
  const topFeature = [...featureCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

  return {
    person,
    status: statusFor(person, lastSeen, now),
    lastSeen,
    current,
    previous,
    activeDays: currentDays.size,
    previousActiveDays: earlierDays.size,
    daily,
    topFeature,
  };
}

function dailyPoints(days: string[], rows: ActivityRow[]): DailyPoint[] {
  const points = new Map(days.map((date) => [date, { date, people: new Set<number>(), changes: 0, views: 0 }]));
  for (const row of rows) {
    const point = points.get(dayOf(row));
    if (!point) continue;
    point.people.add(row.user_id);
    if (row.kind === "page_view") point.views += row.count;
    if (CHANGE_KINDS.has(row.kind)) point.changes += row.count;
  }
  return [...points.values()].map(({ date, people, changes, views }) => ({
    date,
    activePeople: people.size,
    changes,
    views,
  }));
}

const average = (values: number[]) => (values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : 0);

export function summarize(report: UserActivityReport, audience: Audience, now: Date = new Date()): ReportSummary {
  const days = eachDay(report.period.start, report.period.end);
  const previousDays = eachDay(report.period.previous_start, report.period.previous_end);
  const previousDaySet = new Set(previousDays);
  const currentDaySet = new Set(days);

  const audiencePeople = report.people.filter((person) => inAudience(person, audience));
  const audienceIds = new Set(audiencePeople.map((person) => person.user_id));
  const rows = report.activity.filter((row) => audienceIds.has(row.user_id));
  const rowsByUser = Map.groupBy(rows, (row) => row.user_id);

  const people = audiencePeople.map((person) =>
    personStats(person, rowsByUser.get(person.user_id) ?? [], days, previousDaySet, now),
  );
  const activeNow = people.filter((stats) => stats.activeDays > 0);
  const activeBefore = people.filter((stats) => stats.previousActiveDays > 0);

  const currentRows = rows.filter((row) => currentDaySet.has(dayOf(row)));
  const features = FEATURE_MATCHERS.map(({ id, matches }) => {
    const featureRows = currentRows.filter(matches);
    return {
      id,
      people: new Set(featureRows.map((row) => row.user_id)).size,
      actions: featureRows.reduce((sum, row) => sum + row.count, 0),
    };
  });

  const heatmap = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
  for (const row of currentRows) {
    const hour = new Date(row.hour);
    heatmap[(hour.getDay() + 6) % 7]![hour.getHours()]! += row.count;
  }

  const changeTotals = new Map<ActivityKind, number>();
  for (const row of currentRows) {
    if (CHANGE_KINDS.has(row.kind)) changeTotals.set(row.kind, (changeTotals.get(row.kind) ?? 0) + row.count);
  }
  const topChangeKind = [...changeTotals.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

  const withAccess = people.filter((stats) => stats.person.has_access);
  const longestAway =
    withAccess
      .filter((stats) => stats.status === "inactive" || stats.status === "slowing" || stats.status === "never")
      .sort((a, b) => (a.lastSeen?.getTime() ?? 0) - (b.lastSeen?.getTime() ?? 0))[0] ?? null;

  const sum = (key: keyof Totals, period: "current" | "previous") =>
    people.reduce((total, stats) => total + stats[period][key], 0);

  return {
    days,
    previousDays,
    people,
    peopleWithAccess: withAccess.length,
    activePeople: { current: activeNow.length, previous: activeBefore.length },
    activeDaysPerPerson: {
      current: average(activeNow.map((stats) => stats.activeDays)),
      previous: average(activeBefore.map((stats) => stats.previousActiveDays)),
    },
    changes: { current: sum("changes", "current"), previous: sum("changes", "previous") },
    views: { current: sum("views", "current"), previous: sum("views", "previous") },
    daily: dailyPoints(days, rows),
    previousDaily: dailyPoints(previousDays, rows),
    features,
    heatmap,
    topChangeKind,
    longestAway,
  };
}

/** How the work in the period splits between the customer's own team and Trellis staff. */
export function workSplit(report: UserActivityReport): WorkSplit[] {
  const days = new Set(eachDay(report.period.start, report.period.end));
  const trellisIds = new Set(report.people.filter((person) => person.is_trellis).map((person) => person.user_id));
  const split = new Map(WORK_KINDS.map((kind) => [kind, { kind, team: 0, trellis: 0 }]));
  for (const row of report.activity) {
    const entry = split.get(row.kind);
    if (!entry || !days.has(dayOf(row))) continue;
    if (trellisIds.has(row.user_id)) entry.trellis += row.count;
    else entry.team += row.count;
  }
  return [...split.values()].filter((entry) => entry.team + entry.trellis > 0);
}

/** Relative change, or null when there is nothing to compare against. */
export function percentChange({ current, previous }: Comparison): number | null {
  if (previous === 0) return null;
  return (current - previous) / previous;
}
